import type { ReactNode } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { WorkspacePips } from '@/components/layout/workspace-scope-selector'
import type { Workspace } from '@/providers/workspace-provider'
import { cn } from '@/lib/utils'

export type SidebarFilterBarProps = {
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  trailingSlot?: ReactNode
  /** Read-only scope echo (pips for active workspace ids). */
  scopeEcho?: {
    workspaces: Workspace[]
    activeIds: string[]
    onFocusScopePicker?: () => void
  }
  className?: string
  searchTestId?: string
}

/** Search row + trailing actions + optional read-only workspace scope echo. */
export function SidebarFilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  trailingSlot,
  scopeEcho,
  className,
  searchTestId,
}: SidebarFilterBarProps) {
  const showSearch = onSearchChange !== undefined

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 border-b border-border shrink-0 min-w-0',
        className,
      )}
    >
      {showSearch ? (
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-7 pl-7 text-xs"
            data-testid={searchTestId}
          />
        </div>
      ) : null}
      {trailingSlot}
      {scopeEcho && scopeEcho.activeIds.length > 0 ? (
        <button
          type="button"
          onClick={scopeEcho.onFocusScopePicker}
          className={cn(
            'flex h-7 shrink-0 items-center gap-1 rounded-md border border-transparent px-1.5',
            'hover:border-border hover:bg-muted/40 transition-colors',
            !scopeEcho.onFocusScopePicker && 'pointer-events-none',
          )}
          title="Active workspaces"
          aria-label="Active workspaces"
        >
          <WorkspacePips
            workspaces={scopeEcho.workspaces}
            activeIds={scopeEcho.activeIds}
            className="opacity-90"
          />
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {scopeEcho.activeIds.length}
          </span>
        </button>
      ) : null}
    </div>
  )
}
